<script lang="ts" setup>
import type { ComponentExposed } from 'vue-component-type-helpers';
import { InputGenericSingleSelect } from '#components';

const emits = defineEmits<{ created: [address: CreateAssociatedAddressResponseDTO] }>();

defineExpose({
  open
});

const zipCodeSelect = ref<ComponentExposed<typeof InputGenericSingleSelect<number>> | null>(null);

const isOpen = ref<boolean>(false);

const { t } = useEkI18n();
const { CreateAssociatedAddress } = useAddressApi();
const { data: zipCodes, pending, refresh } = useApiFetch<ZipCodeResponseDTO[]>('V3/Address/ZipCodes', { immediate: false });

const selectedZipCode = ref<ZipCodeResponseDTO | null>(null);
const zipCodeIsInvalid = computed(() => selectedZipCode.value === null);
const zipCodeTouched = ref<boolean>(false);

const address = ref<string>('');
const addressIsInvalid = computed(() => address.value === '');
const addressTouched = ref<boolean>(false);

const address2 = ref<string>('');
const address2Touched = ref<boolean>(false);

const zipCodeText = computed(() => {
  if (selectedZipCode.value === null) {
    return '';
  }

  return `${selectedZipCode.value.Code} ${selectedZipCode.value.CityName} (${selectedZipCode.value.Country.CountryName})`;
});

async function createAddress() {
  if (addressIsInvalid.value || zipCodeIsInvalid.value || selectedZipCode.value === null) {
    addressTouched.value = true;
    address2Touched.value = true;
    zipCodeTouched.value = true;

    return;
  }

  const addressData: CreateAssociatedAddressRequestDTO = {
    Address: address.value,
    Address2: address2.value,
    ZipCodeID: selectedZipCode.value.ID
  };

  const response = await CreateAssociatedAddress(addressData);

  if (response === undefined || response.HasErrors === true) {
    return;
  }

  emits('created', response.Data);

  close();
}

function reset() {
  addressTouched.value = false;
  address2Touched.value = false;
  zipCodeTouched.value = false;

  address.value = '';
  address2.value = '';
  selectedZipCode.value = null;
}

function open() {
  refresh();
  isOpen.value = true;
}

function close() {
  isOpen.value = false;
  reset();
}

</script>

<template>
  <IonModal
    :is-open="isOpen"
    class="ion-padding modal"
    @did-dismiss="isOpen = false, reset();"
  >
    <IonToolbar>
      <IonTitle>{{ t('input.createNewAddress') }}</IonTitle>
    </IonToolbar>

    <InputContainer
      lines="none"
      icon="fa-light fa-fw fa-circle-1"
    >
      <IonInput
        v-model.trim="address"
        :class="{ 'ion-invalid': addressIsInvalid, 'ion-touched': addressTouched}"
        :label="t('worksheet.billingAddress')"
        label-placement="floating"
        :error-text="t('worksheet.billingAddressRequired')"
        @ion-blur="addressTouched = true"
      />
    </InputContainer>

    <InputContainer
      lines="none"
      icon="fa-light fa-fw fa-circle-2"
    >
      <IonInput
        v-model.trim="address2"
        :class="{ 'ion-touched': address2Touched}"
        :label="t('worksheet.billingAddress2')"
        label-placement="floating"
        error-text="_"
        @ion-blur="address2Touched = true"
      />
    </InputContainer>

    <InputContainer
      icon="fa-light fa-fw fa-building-flag"
      lines="none"
      button
      :loading="pending"
      @click="zipCodeSelect?.open()"
    >
      <IonInput
        :value="zipCodeText"
        :class="{ 'ion-invalid': zipCodeIsInvalid, 'ion-touched': zipCodeTouched}"
        :label="t('worksheet.zipCode')"
        label-placement="floating"
        :error-text="t('input.zipCodeRequired')"
        readonly
        @ion-blur="zipCodeTouched = true"
      />
    </InputContainer>

    <IonRow class="sm-padding">
      <IonCol>
        <IonButton
          expand="block"
          fill="outline"
          shape="round"
          @click="close"
        >
          {{ t('buttons.cancel') }}
        </IonButton>
      </IonCol>
      <IonCol>
        <IonButton
          expand="block"
          shape="round"
          @click="createAddress"
        >
          {{ t('buttons.create') }}
        </IonButton>
      </IonCol>
    </IonRow>
  </IonModal>

  <InputGenericSingleSelect
    ref="zipCodeSelect"
    :items="zipCodes?.Data ?? []"
    :title="t('input.selectZipCode')"
    :resolve-key="zip => zip.ID"
    :searchable-fields="[
      'Code',
      'CityName',
      (zip) => zip.Country.Description,
      (zip) => zip.Country.CountryName,
    ]"
    @select="(selected)=>selectedZipCode = selected"
  >
    <template #item="{ item: zip }">
      {{ `${zip.Code} ${zip.CityName} (${zip.Country.CountryName})` }}
    </template>
  </InputGenericSingleSelect>
</template>

<style scoped>
.modal {
  --border-radius: 16px;
  --height: auto;
}

.sm-padding {
  padding: 8px;
}
</style>
